import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RecommendedHint from '../components/RecommendedHint';
import { FormattedCurrencyInput } from '../components/FormattedInput';
import { 
  Home, 
  Bed, 
  Bath, 
  Square, 
  Calendar, 
  Building2, 
  DollarSign,
  ChevronRight,
  ChevronDown,
  AlertCircle,
  Loader2,
  ArrowLeft,
  Wrench,
  TrendingUp,
  Building,
  CreditCard,
  Banknote,
  Settings
} from 'lucide-react';
import { analysisAPI, recentAnalysesAPI } from '../api';
import { UserContext } from '../App';
import { toast } from 'sonner';
import UsageLimitReached from '../components/UsageLimitReached';
import { getApiErrorMessage } from '../utils/apiError';
import { parseFormattedNumber } from '../utils/formatNumber';
import { buildMfIncomeFields, unitsFromType, isMultiFamilyType } from '../utils/mfIncomeFields';
import { RENTAL_CONDITIONS } from '../utils/rentalMath';
import { DEFAULT_RENTAL_ORIGINATION_POINTS } from '../utils/financingDefaults';

/**
 * PropertyConfirmation - Pre-analysis step
 * 
 * This page appears AFTER address input but BEFORE analysis runs.
 * User can:
 * 1. Review fetched property data
 * 2. Correct any inaccuracies
 * 3. Select strategy and rehab inputs
 * 4. Click "Run Analysis" to proceed
 * 
 * This ensures:
 * - Correct property type = correct comparables
 * - No wasted API calls
 * - Clean state management
 */

const PropertyConfirmation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = React.useContext(UserContext);
  
  // Get data passed from Analyzer
  const initialData = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [lang] = useState(initialData.lang || 'en');
  
  // Map rental property type from Analyzer to standard property_type
  const propertyTypeMap = {
    'sfr': 'Single Family',
    'duplex': 'Duplex',
    'triplex': 'Triplex',
    'fourplex': 'Fourplex'
  };
  
  // V5.1: Smart default for generic Multi-Family when unit count is unknown
  // Uses sqft and beds to estimate likely unit count
  const getSmartPropertyTypeDefault = (propData) => {
    const rawType = propData?.property_type || 'Single Family';
    const needsConfirmation = propData?.needs_unit_confirmation;
    const sqft = propData?.sqft || 0;
    const beds = propData?.beds || 0;
    
    // If not generic Multi-Family or doesn't need confirmation, use raw type
    if (!needsConfirmation && rawType !== 'Multi-Family') {
      return rawType;
    }
    
    // For generic Multi-Family, estimate based on sqft and beds
    // Typical unit sizes: Duplex ~800-1200sqft/unit, Triplex ~700-1000sqft/unit
    if (rawType === 'Multi-Family' || needsConfirmation) {
      // Heuristic: sqft per unit estimates
      // < 2000 sqft with 4-6 beds = likely Duplex
      // 2000-3000 sqft with 6-9 beds = likely Triplex  
      // > 3000 sqft with 8+ beds = likely Fourplex
      
      if (sqft > 0 && beds > 0) {
        const avgBedsPerUnit = beds / 2; // Assume 2-3 beds per unit avg
        
        if (sqft <= 2200 && beds <= 6) {
          return 'Duplex';
        } else if (sqft <= 3200 && beds <= 9) {
          return 'Triplex';
        } else if (sqft <= 4500 && beds <= 12) {
          return 'Fourplex';
        }
      }
      
      // Default to Duplex (most common) if we can't determine
      // Better than Multi-Family 5+ which is rare for small investors
      return 'Duplex';
    }
    
    return rawType;
  };
  
  // Editable property fields
  const [property, setProperty] = useState({
    address: initialData.property?.address || '',
    city: initialData.property?.city || '',
    state: initialData.property?.state || '',
    zip_code: initialData.property?.zip_code || '',
    property_type: (initialData.propertyTypeForRental && initialData.strategy === 'rental')
      ? (propertyTypeMap[initialData.propertyTypeForRental] || getSmartPropertyTypeDefault(initialData.property))
      : getSmartPropertyTypeDefault(initialData.property),
    beds: initialData.property?.beds || 3,
    baths: initialData.property?.baths || 2,
    sqft: initialData.property?.sqft || 1500,
    year_built: initialData.property?.year_built || 1980,
    listing_price: initialData.askingPrice || initialData.property?.listing_price || 0,
    rent_estimate: initialData.property?.rent_estimate || 0,
    normalized_address: initialData.property?.normalized_address || '',
    listing_url: initialData.property?.listing_url || '',
    listing_source: initialData.property?.listing_source || 'manual',
    needs_unit_confirmation: initialData.property?.needs_unit_confirmation || false,
    // Low-confidence type detection (e.g. no property record, or a source that may
    // mislabel duplexes as SFR). Drives the prominent "verify property type" banner.
    needs_type_verification: initialData.property?.needs_type_verification || false,
    type_confidence: initialData.property?.type_confidence || null,
  });
  
  // Strategy and rehab settings
  const [strategy, setStrategy] = useState(initialData.strategy || 'flip');
  // 'cash' or 'financed'. For a rental the equivalent choice may already have been made
  // upstream as rentalFinancingType ('cash' | 'dscr'); seed from it so the toggle shows
  // what will actually be analyzed. Without this the toggle defaulted to 'cash' while the
  // rental payload defaulted to 'dscr', so an untouched form displayed Cash and analyzed
  // a loan.
  const [financingMode, setFinancingMode] = useState(
    initialData.financingMode
    || (initialData.rentalFinancingType === 'dscr' ? 'financed' : null)
    || 'cash'
  );
  // Read-only: nothing on this screen sets the subject's condition any more. It is still
  // carried and still sent, explicitly, so the value the backend uses comes from the
  // client rather than from server.py's `or 'average'` fallback.
  const [propertyCondition] = useState(initialData.propertyCondition || 'average');
  // Read-only now that the presets are gone: it is still carried and still sent, so a
  // saved analysis with a declared condition opens on it, but nothing on this screen sets it.
  const [rehabCondition] = useState(initialData.rehabCondition || 'auto');
  const [customRehabTotal, setCustomRehabTotal] = useState(initialData.customRehabTotal || '');
  const [customRehabPerSqft, setCustomRehabPerSqft] = useState(initialData.customRehabPerSqft || '');
  const [customRehabInputMode, setCustomRehabInputMode] = useState('total');
  const [targetRule, setTargetRule] = useState(initialData.targetRule || 70);

  // MULTI-FAMILY ARV inputs (necessary DATA for a real multi-family result):
  // after-repair rent/unit + GRM. These drive the income-led ARV. Shown only for
  // multi-family types. unit_count derives from the property type.
  // unitsFromType / isMultiFamilyType now live in utils/mfIncomeFields.js,
  // shared with the payload builder so the panel's gate and the request cannot drift.
  // Seeded from the user's OWN per-unit rent when the Analyzer collected one, ahead of
  // any estimate. Without this the panel opens showing a provider estimate, and once it
  // starts feeding the payload (see the MF branch below) that estimate would silently
  // replace a rent the user had already typed.
  //
  // NO FALLBACK TO `rent_estimate`. That field is RentCast's /avm/rent/long-term figure,
  // and it is the rent of the WHOLE BUILDING, not of one unit. Measured, not assumed, on
  // 252 Philip St (Detroit 48215): RentCast's own record for it is 6 beds / 2,358 sqft —
  // the entire structure — and it priced that envelope against whole houses of 2,000-2,844
  // sqft renting $2,200-2,800, returning $2,420. The single UNITS in its own comparable
  // set ("674 Alter Rd, Fl 2", "468 Alter Rd, Fl 1") are 900-1,250 sqft. The response
  // carries no unit field at all: the model has no concept of units.
  //
  // Seeding a per-unit field with that number, which the payload builder then multiplies
  // by the unit count, produced a rent of exactly 2x the truth ($4,840 against $2,420) —
  // and this panel calls that field the one that determines the ARV.
  //
  // BLANK rather than `rent_estimate / units`: dividing inherits RentCast's premise that a
  // 6-bed envelope rents as one tenancy, and two 3-bed units do not rent for half of a
  // hypothetical 6-bed rental — small units rent at a HIGHER $/sqft. That makes the
  // division an inference on top of an inference, landing a plausible-looking number in
  // the field that carries 80% of the ARV weight once a GRM is typed. An empty field is
  // visible; a credible wrong number is not. The panel already tells the user blank is a
  // legitimate choice, and blank is the appraiser-validated default.
  //
  // The FIRST source below stays: income_approach.monthly_rent_per_unit really is
  // per-unit — the engine divided it itself.
  const [mfRentPerUnit, setMfRentPerUnit] = useState(() => {
    const total = Number(initialData.totalUserRent) || 0;
    const units = Number(initialData.unitCount) || 0;
    if (total > 0 && units > 0) return Math.round(total / units);
    return initialData.property?.arv_details?.income_approach?.monthly_rent_per_unit || '';
  });
  // GRM: BLANK by default (no seeded 85). Multifamily is SALES-FIRST — leaving GRM
  // blank lets the engine value by sales comparables (the appraiser-validated default
  // across 12 appraisals / 5 states). Typing a GRM is an EXPLICIT opt-in to value by
  // rents (after-repair), which the UI labels clearly.
  //
  // AND IT REALLY IS BLANK NOW. It used to seed from `income_approach.grm` — the GRM the
  // engine DERIVED from as-is comps — and buildMfIncomeFields then sent that back as
  // `multi_family_grm`, which the backend reads as the user's override. So the "explicit
  // opt-in" described above happened by default, without anyone typing. See
  // tests/mf_grm_promotion_impact.py: promoting a derived GRM takes the median |error|
  // from 15.5% to 32.4% on the three properties that have one.
  const [mfGrm, setMfGrm] = useState('');
  
  // Rental-specific fields from Analyzer
  const totalUserRent = initialData.totalUserRent || 0;
  const userRentInputs = initialData.userRentInputs || [];
  const unitCount = initialData.unitCount || 1;
  // Derived from the Cash/Financed toggle, NOT frozen from initialData. This used to be a
  // const the toggle never wrote to, so a rental was always analyzed as a DSCR loan
  // whatever the user picked — and the backend then scored a phantom debt service against
  // it. 'financed' maps to 'dscr' because DSCR is the only rental loan product offered.
  //
  // A REFINANCE is always DSCR — the same invariant Analyzer.js:1362 enforces
  // ("Always DSCR for refi"). An owner refinancing buys nothing, so there is no cash
  // purchase to model: rental_engine.py:424 takes the cash branch BEFORE the refinance
  // branch at :454, so 'cash' here would skip the refi handling entirely and report a
  // cash-out figure next to "cash invested = full price" with no loan.
  // Read from initialData rather than the rentalPurchaseType const below: that one is
  // declared later in this same scope, so referencing it here would hit the temporal
  // dead zone.
  const isRefinance = initialData.rentalPurchaseType === 'refinancing';
  const rentalFinancingType = (financingMode === 'cash' && !isRefinance) ? 'cash' : 'dscr';
  const rentalDownPaymentPct = initialData.rentalDownPaymentPct ?? 25;
  const rentalInterestRate = initialData.rentalInterestRate || 7.5;
  // STATE, not a frozen const. This is the field that governs DSCR eligibility, the
  // forced-cash fallback and the per-mode rehab figure — and for a long time nothing on
  // this screen could write it, so a rental the user marked distressed still went out as
  // turnkey: DSCR-eligible, financed as a loan. Measured in production.
  //
  // It is now the ONLY condition control in the product. The generic scale it used to sit
  // beside is gone: it changed no number in flip, and in rentals it let the same house be
  // called turnkey and distressed at once.
  const [rentalPropertyCondition, setRentalPropertyCondition] =
    useState(initialData.rentalPropertyCondition || 'turnkey');

  // FEATURE GAPS the user declares against the LOCAL comparables. null = not declared,
  // which means "no gap" — never "unknown". Phrased as a difference from the
  // neighbourhood rather than an absent feature, so that in a market where nothing has a
  // basement the user simply leaves it unticked and nothing is ever deducted.
  // Values are the exact shapes the backend reads: missing_garage is an INT (1 or 2),
  // missing_basement a STRING ('unfinished' | 'finished'). The dollar amounts live only
  // in analysis_engine.FEATURE_GAP_ADJUSTMENTS and are never sent here or shown.
  const [missingBasement, setMissingBasement] = useState(initialData.missingBasement ?? null);
  const [missingGarage, setMissingGarage] = useState(initialData.missingGarage ?? null);
  const annualPropertyTaxes = initialData.annualPropertyTaxes || 0;
  const annualInsurance = initialData.annualInsurance || 0;
  const rentalClosingCostsPct = initialData.rentalClosingCostsPct || 3;
  const rentalOriginationPoints = initialData.rentalOriginationPoints || DEFAULT_RENTAL_ORIGINATION_POINTS;
  
  // New rental fields
  const rentalPurchaseType = initialData.rentalPurchaseType || 'new_purchase';
  const appraisedValue = initialData.appraisedValue || '';
  const existingPayoff = initialData.existingPayoff || '0';
  const refiLTV = initialData.refiLTV || 75;
  const rentalMinCashFlow = initialData.rentalMinCashFlow || 200;
  const rentalMinDscr = initialData.rentalMinDscr || 1.25;
  const rentalMinCapRate = initialData.rentalMinCapRate || 6;
  const rentalToleranceMode = initialData.rentalToleranceMode || 'balanced';
  const rentalLenderFees = initialData.rentalLenderFees || '';
  const rentalTitleAttorneyFees = initialData.rentalTitleAttorneyFees || '';
  
  // Advanced settings collapsed state (hidden by default)
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  // Validation state
  const [errors, setErrors] = useState({});

  // Analysis-limit (402) state
  const [limitReached, setLimitReached] = useState(false);
  const [usageInfo, setUsageInfo] = useState(null);

  const propertyTypes = [
    { value: 'Single Family', label: 'Single Family' },
    { value: 'Duplex', label: 'Duplex' },
    { value: 'Triplex', label: 'Triplex' },
    { value: 'Fourplex', label: 'Fourplex' },
    // "Multi-Family (5+)" was removed 2026-08-11. It carried three different unit
    // counts for one choice — the label said 5+, unitsFromType() returned 4 and the
    // backend's CONFIRMED_TYPE_MAP said 5 — and "5+" is an open range, so no value is
    // defensible: a 5-unit and a 40-unit building selected the same option. The
    // multi-family valuation is calibrated against Fannie Mae Form 1025, which covers
    // 2-4 units, so a 5+ subject should not use that path at all. The four remaining
    // options each imply an exact count. getSmartPropertyTypeDefault never returned
    // this value anyway — it maps a generic provider type onto Duplex/Triplex/Fourplex.
    { value: 'Condo', label: 'Condo/Townhouse' },
  ];

  // Format number with commas
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!property.address) newErrors.address = 'Address is required';
    if (!property.beds || property.beds < 1) newErrors.beds = 'At least 1 bedroom required';
    if (!property.baths || property.baths < 1) newErrors.baths = 'At least 1 bathroom required';
    if (!property.sqft || property.sqft < 100) newErrors.sqft = 'Square footage required';
    // Skip listing_price validation for valuation mode (price is optional)
    if (initialData.strategy !== 'valuation' && (!property.listing_price || property.listing_price < 1000)) {
      newErrors.listing_price = 'Valid asking price required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Run the analysis
  const handleRunAnalysis = async () => {
    if (!validateForm()) {
      toast.error(lang === 'en' ? 'Please fix the errors below' : 'Por favor corrija los errores');
      return;
    }
    
    setAnalyzing(true);
    
    try {
      // V5.0: Load user's classification criteria from localStorage
      let classificationCriteria = { min_profit: 20000, min_margin: 10 };
      try {
        const savedCriteria = localStorage.getItem('chekdeal_classification_criteria');
        if (savedCriteria) {
          classificationCriteria = JSON.parse(savedCriteria);
        }
      } catch (e) {
        console.warn('Failed to load classification criteria from localStorage');
      }
      
      // Build the analysis request with confirmed property data
      const requestData = {
        input_type: 'address',
        input_value: property.normalized_address || property.address,
        user_id: user?.user_id || 'guest',  // Required for subscription tracking
        asking_price: Number(property.listing_price),
        strategy: strategy,
        property_condition: propertyCondition,
        rehab_condition: rehabCondition,
        language: lang,
        // Pass confirmed property details as overrides
        property_overrides: {
          property_type: property.property_type,
          beds: Number(property.beds),
          baths: Number(property.baths),
          sqft: Number(property.sqft),
          year_built: Number(property.year_built),
        },
        // Target rule for flip
        target_rule: targetRule / 100,
        // Financing mode - determines loan costs in analysis
        purchase_financing: financingMode,
        rehab_financing: financingMode,
        // V5.0: Pass user's classification criteria for dynamic decision text
        min_profit: classificationCriteria.min_profit || 20000,
        min_margin: classificationCriteria.min_margin || 10,
        // USER-DECLARED FEATURE GAPS — top level, NOT inside the rental spread below.
        // They apply to every strategy: the deduction lands on the ARV, which flip and
        // valuation depend on just as much as rental. (First written inside that spread,
        // where a flip analysis dropped them silently and the DOM test caught it.)
        missing_basement: missingBasement,
        missing_garage: missingGarage,
        // Rental-specific fields (only included when strategy is rental)
        ...(strategy === 'rental' && {
          user_rent: totalUserRent,
          rent_inputs: userRentInputs.map(r => parseFloat(r) || 0),
          unit_count: unitCount,
          rental_financing_type: rentalFinancingType,
          rental_down_payment_pct: rentalDownPaymentPct,
          rental_interest_rate: rentalInterestRate,
          rental_property_condition: rentalPropertyCondition,
          annual_property_taxes: annualPropertyTaxes,
          annual_insurance: annualInsurance,
          rental_closing_costs_pct: rentalClosingCostsPct,
          rental_origination_points: rentalOriginationPoints,
          // New rental fields
          rental_purchase_type: rentalPurchaseType,
          appraised_value: appraisedValue ? parseFloat(appraisedValue) : null,
          existing_payoff: parseFloat(existingPayoff) || 0,
          refi_ltv: refiLTV,
          // BUG C: this was posted as `rental_min_cashflow`, but the API model field is
          // `rental_min_cash_flow` — FastAPI dropped the unknown key and the user's own
          // cash-flow target was silently replaced by a default on every first analysis.
          // Both spellings are sent so older/newer backends agree.
          rental_min_cash_flow: rentalMinCashFlow,
          rental_min_cashflow: rentalMinCashFlow,
          rental_min_dscr: rentalMinDscr,
          rental_min_cap_rate: rentalMinCapRate,
          rental_tolerance_mode: rentalToleranceMode,
          rental_lender_fees: parseFloat(rentalLenderFees) || 0,
          rental_title_attorney_fees: parseFloat(rentalTitleAttorneyFees) || 0,
        }),
        // MULTI-FAMILY ARV inputs — necessary data for a real income-led ARV.
        // GRM flows for every strategy; rent/units for non-rental multi (rental
        // already sends its own user_rent/unit_count above).
        ...buildMfIncomeFields({
          strategy,
          propertyType: property.property_type,
          mfRentPerUnit,
          mfGrm,
          analyzerUnitCount: unitCount,
        }),
      };
      
      // Rehab is AUTHORITATIVE: if the user entered a custom budget, ALWAYS use it —
      // never let the condition-based estimate silently replace a number the user typed.
      // (Previously gated on rehabCondition==='custom'; a total carried from the Analyzer
      // with condition 'auto' was dropped, so $100k entered became a ~$70k estimate.)
      const enteredTotal = parseFloat(String(customRehabTotal).replace(/[$,\s]/g, ''));
      const enteredPerSqft = parseFloat(String(customRehabPerSqft).replace(/[$,\s]/g, ''));
      if (customRehabInputMode === 'total' && enteredTotal > 0) {
        requestData.custom_rehab_total = enteredTotal;
      } else if (customRehabInputMode === 'persqft' && enteredPerSqft > 0) {
        requestData.custom_rehab_per_sqft = enteredPerSqft;
      }
      
      // Run the analysis
      const response = await analysisAPI.analyze(requestData);
      
      if (response.data) {
        // V5.0: Navigate to results with EXPLICIT analysis_id in URL
        // This ensures fresh analysis cannot be overridden by stale sessionStorage
        const analysisId = response.data.analysis_id || response.data.id;
        
        // Clear any stale session data before navigation
        try {
          sessionStorage.removeItem('chekdeal_last_analysis');
          sessionStorage.removeItem('chekdeal_fresh_analysis_id');
          // Drop any working state for THIS analysis id. Re-analyzing the same property
          // returns the same (cached) analysis_id, so an earlier run's edits would still
          // be sitting under that key — and Results treats "a working state exists" as
          // "this is a re-entry, prefer the saved edits over the confirmation inputs".
          // Without this, re-running a property you had previously edited would silently
          // restore those old edits over what you just typed on this screen.
          if (analysisId) sessionStorage.removeItem(`chekdeal_working_${analysisId}`);
          sessionStorage.removeItem('chekdeal_criteria_return_state');
          sessionStorage.removeItem('chekdeal_criteria_return_id');
        } catch (e) {
          console.warn('Failed to clear sessionStorage:', e);
        }
        
        // Dispatch usage-updated event to refresh header counter
        try {
          window.dispatchEvent(new Event('usage-updated'));
        } catch (e) {
          console.warn('Failed to dispatch usage-updated event:', e);
        }
        
        // Navigate with explicit analysis_id in route
        navigate(`/results/${analysisId}`, {
          state: {
            analysis: response.data,
            fromConfirmation: true,
            freshAnalysisId: analysisId,  // Explicit marker
            initialFinancingMode: financingMode,
            // Carry the ACTUAL rehab/condition inputs so Results seeds from what the user
            // entered (not re-derived from the response or stale sessionStorage). This is
            // what makes the entered rehab authoritative through recalcs.
            rehabCondition: rehabCondition,
            customRehabTotal: requestData.custom_rehab_total || null,
            customRehabPerSqft: requestData.custom_rehab_per_sqft || null,
            customRehabInputMode: customRehabInputMode,
            propertyCondition: propertyCondition,
            // BUG 4: carry the rental FEE inputs the user just entered. They were sent to
            // the API (and charged against cash invested) but never handed to the Results
            // page, so its financing section showed hardcoded defaults and the FIRST
            // recalc silently overwrote the real fees with 3% / 1pt / $0.
            // Only these four are carried: every other rental input (financing type, down
            // payment, rate, condition, taxes, rent) comes back inside rental_analysis and
            // is recovered from there. Fees do not — the response has only their summed
            // dollar cost, not the inputs that produced it.
            ...(strategy === 'rental' && {
              rentalClosingCostsPct: rentalClosingCostsPct,
              rentalOriginationPoints: rentalOriginationPoints,
              rentalLenderFees: parseFloat(rentalLenderFees) || 0,
              rentalTitleAttorneyFees: parseFloat(rentalTitleAttorneyFees) || 0,
            }),
          },
          replace: true  // Replace history to prevent back-button confusion
        });
        
        // Auto-save to recent history (non-blocking)
        try {
          if (user?.user_id) {
            await recentAnalysesAPI.addRecent({
              user_id: user.user_id,
              analysis_data: response.data,
              strategy: strategy,
              arv_mode: null
            });
          }
        } catch (recentError) {
          console.warn('Failed to save to recent history:', recentError);
        }
      }
      
    } catch (error) {
      console.error('Analysis error:', error);
      // 402 = analysis limit reached. The backend sends detail as an OBJECT
      // ({message, usage, upgrade_url}); render the upgrade UI instead of
      // toasting the object (which would crash the page as a React child).
      // 401 SIGNUP_REQUIRED: an anonymous visitor reached the analyze call for a
      // non-sample address. Send them to sign up rather than showing an upgrade wall
      // (they have no account yet, so there is nothing to upgrade).
      if (error.response?.status === 401
          && error.response?.data?.detail?.code === 'SIGNUP_REQUIRED') {
        toast.error(getApiErrorMessage(error, 'Create a free account to analyze this property.'));
        navigate('/analyze', { state: { ...initialData, signupRequired: true } });
        return;
      }

      if (error.response?.status === 402) {
        setUsageInfo(error.response.data?.detail?.usage || null);
        setLimitReached(true);
        return;
      }
      toast.error(getApiErrorMessage(error, 'Analysis failed. Please try again.'));
    } finally {
      setAnalyzing(false);
    }
  };
  
  // Go back to analyzer
  const handleBack = () => {
    navigate('/analyzer', { state: { property, strategy, propertyCondition } });
  };

  // If no property data, redirect to analyzer
  useEffect(() => {
    if (!initialData.property && !property.address) {
      navigate('/analyzer');
    }
  }, [initialData, property.address, navigate]);

  if (limitReached) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-background to-background/95" data-testid="confirm-limit-reached">
        <UsageLimitReached
          usage={usageInfo}
          onViewPricing={() => navigate('/pricing')}
          onViewHistory={() => navigate('/deals')}
          lang={lang}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background/95">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <button 
            onClick={handleBack}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors"
            data-testid="back-to-analyzer"
          >
            <ArrowLeft className="w-4 h-4" />
            {lang === 'en' ? 'Back to Search' : 'Volver a Buscar'}
          </button>
          
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {lang === 'en' ? 'Confirm Property Details' : 'Confirmar Detalles de Propiedad'}
          </h1>
          <p className="text-muted-foreground">
            {lang === 'en' 
              ? 'Review and correct property information before analysis. Accurate details = better comparables.'
              : 'Revise y corrija la información antes del análisis. Detalles precisos = mejores comparables.'}
          </p>
        </div>
        
        {/* Property Address Display */}
        <div className="bg-card rounded-xl border border-border p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-primary/10 rounded-lg">
              <Home className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-foreground">
                {property.address}
              </h2>
              <p className="text-muted-foreground">
                {property.city}, {property.state} {property.zip_code}
              </p>
            </div>
          </div>
        </div>
        
        {/* ============================================== */}
        {/* SAFETY NET: prominent "verify property type" banner when detection */}
        {/* confidence is low (e.g. no property record, or a source that may   */}
        {/* mislabel a duplex as Single Family). Wrong type => wrong comps =>   */}
        {/* wrong ARV, so we ask the user to confirm BEFORE analysis runs.      */}
        {/* ============================================== */}
        {property.needs_type_verification && (
          <div
            className="rounded-xl border-2 border-amber-400 bg-amber-50 p-5 mb-6 flex items-start gap-3"
            data-testid="verify-property-type-banner"
          >
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-base font-bold text-amber-900">
                {lang === 'en'
                  ? `⚠️ Verify property type — we're not certain this is ${property.property_type}`
                  : `⚠️ Verifique el tipo de propiedad — no estamos seguros de que sea ${property.property_type}`}
              </h3>
              <p className="text-sm text-amber-800 mt-1">
                {lang === 'en'
                  ? 'We could not confirm the property type from public records. If this is a duplex, triplex, or multi-family, select the correct type below — it drives the comparables and the ARV.'
                  : 'No pudimos confirmar el tipo de propiedad con los registros públicos. Si es un dúplex, tríplex o multifamiliar, seleccione el tipo correcto abajo — determina los comparables y el ARV.'}
              </p>
            </div>
          </div>
        )}

        {/* ============================================== */}
        {/* PRIORITY SECTION: Property Details + Financing */}
        {/* These drive comparable selection - MUST be correct */}
        {/* ============================================== */}
        <div className="bg-card rounded-xl border-2 border-primary/30 p-6 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-primary" />
            <h3 className="text-lg font-semibold text-foreground">
              {lang === 'en' ? 'Property Details' : 'Detalles de Propiedad'}
            </h3>
          </div>
          <p className="text-sm text-primary/80 mb-4 font-medium">
            {lang === 'en' 
              ? 'Confirm property details first — these drive comparable selection'
              : 'Confirma los detalles primero — estos determinan la selección de comparables'}
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {/* Property Type - CRITICAL for comps */}
            <div className="col-span-2 md:col-span-1">
              <label className="block text-sm font-medium text-foreground mb-1">
                <Building2 className="w-4 h-4 inline mr-1" />
                {lang === 'en' ? 'Property Type' : 'Tipo de Propiedad'}
                <span className="text-red-500 ml-1">*</span>
                {property.needs_unit_confirmation && (
                  <span className="ml-2 text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full">
                    {lang === 'en' ? 'Please confirm' : 'Por favor confirme'}
                  </span>
                )}
              </label>
              <select
                value={property.property_type}
                onChange={(e) => setProperty(p => ({ ...p, property_type: e.target.value, needs_unit_confirmation: false, needs_type_verification: false }))}
                className={`w-full px-3 py-2.5 border-2 rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 font-medium ${(property.needs_unit_confirmation || property.needs_type_verification) ? 'border-amber-400 ring-2 ring-amber-200' : 'border-primary/30'}`}
                data-testid="property-type-select"
              >
                {propertyTypes.map(type => (
                  <option key={type.value} value={type.value}>{type.label}</option>
                ))}
              </select>
              {property.needs_unit_confirmation && (
                <p className="text-amber-600 text-xs mt-1">
                  {lang === 'en'
                    ? 'Data shows multi-family but unit count is unclear. Please select the correct type.'
                    : 'Los datos muestran multifamiliar pero el número de unidades no está claro. Seleccione el tipo correcto.'}
                </p>
              )}
              {/* PROVIDER TYPE WARNING.
                  Measured over the 29 multi-family properties in our own appraisal
                  corpus: the data provider named the type correctly zero times, and 11
                  of them came back as "Single Family". The type selector is the only
                  control that changes the valuation path — a unit count alone does
                  nothing if the type stays single-family — so the copy points there.
                  SHOWN FOR SINGLE-FAMILY TOO, on purpose: that is where the provider's
                  error lives and where a user has no reason to suspect anything. Gating
                  it on an already-multi-family type would hide it in exactly the case it
                  exists for. Only Condo/Townhouse is excluded — not a 2-4 unit candidate.
                  Amber and never blocking: this is a verification, not an error. */}
              {property.property_type !== 'Condo' && (
                <div className="mt-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2"
                     data-testid="property-type-warning">
                  <p className="text-xs font-semibold text-amber-800">
                    {lang === 'en' ? 'Double-check the property type' : 'Verifica el tipo de propiedad'}
                  </p>
                  <p className="text-xs text-amber-700 mt-0.5 leading-snug">
                    {lang === 'en'
                      ? 'Our data provider often reports 2-4 unit buildings as single-family. If this is a duplex, triplex or fourplex, select that property type above — it changes how we value the property.'
                      : 'Nuestro proveedor de datos suele reportar edificios de 2-4 unidades como unifamiliares. Si es un dúplex, triplex o fourplex, selecciona ese tipo de propiedad arriba — cambia cómo valuamos la propiedad.'}
                  </p>
                  <p className="text-xs text-amber-700 mt-1 leading-snug" data-testid="property-type-warning-5plus">
                    {lang === 'en'
                      ? "Have 5 or more units? This tool isn't calibrated for that yet — "
                      : '¿Tiene 5 unidades o más? Esta herramienta aún no está calibrada para eso — '}
                    <a href="/contact" className="underline font-medium hover:text-amber-900">
                      {lang === 'en' ? 'get in touch' : 'contáctanos'}
                    </a>
                    .
                  </p>
                </div>
              )}
            </div>
            
            {/* Beds */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <Bed className="w-4 h-4 inline mr-1" />
                {lang === 'en' ? 'Bedrooms' : 'Habitaciones'}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                value={property.beds}
                onChange={(e) => setProperty(p => ({ ...p, beds: e.target.value }))}
                className={`w-full px-3 py-2.5 border-2 rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 font-medium ${errors.beds ? 'border-red-500' : 'border-primary/30'}`}
                min="1"
                max="20"
                data-testid="beds-input"
              />
              {errors.beds && <p className="text-red-500 text-xs mt-1">{errors.beds}</p>}
            </div>
            
            {/* Baths */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <Bath className="w-4 h-4 inline mr-1" />
                {lang === 'en' ? 'Bathrooms' : 'Baños'}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                value={property.baths}
                onChange={(e) => setProperty(p => ({ ...p, baths: e.target.value }))}
                className={`w-full px-3 py-2.5 border-2 rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 font-medium ${errors.baths ? 'border-red-500' : 'border-primary/30'}`}
                min="1"
                max="10"
                step="0.5"
                data-testid="baths-input"
              />
              {errors.baths && <p className="text-red-500 text-xs mt-1">{errors.baths}</p>}
            </div>
            
            {/* SqFt */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <Square className="w-4 h-4 inline mr-1" />
                {lang === 'en' ? 'Square Feet' : 'Pies Cuadrados'}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <input
                type="number"
                value={property.sqft}
                onChange={(e) => setProperty(p => ({ ...p, sqft: e.target.value }))}
                className={`w-full px-3 py-2.5 border-2 rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 font-medium ${errors.sqft ? 'border-red-500' : 'border-primary/30'}`}
                min="100"
                max="50000"
                data-testid="sqft-input"
              />
              {errors.sqft && <p className="text-red-500 text-xs mt-1">{errors.sqft}</p>}
            </div>
            
            {/* Year Built */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">
                <Calendar className="w-4 h-4 inline mr-1" />
                {lang === 'en' ? 'Year Built' : 'Año de Construcción'}
              </label>
              <input
                type="number"
                value={property.year_built}
                onChange={(e) => setProperty(p => ({ ...p, year_built: e.target.value }))}
                className="w-full px-3 py-2.5 border-2 border-primary/30 rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 font-medium"
                min="1800"
                max={new Date().getFullYear()}
                data-testid="year-built-input"
              />
            </div>
            
            {/* Purchase Price - Hidden for valuation mode */}
            {initialData.strategy !== 'valuation' && (
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  <DollarSign className="w-4 h-4 inline mr-1" />
                  {lang === 'en' ? 'Purchase Price' : 'Precio de Compra'}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-medium">$</span>
                  <input
                    type="text"
                    value={property.listing_price ? formatNumber(property.listing_price) : ''}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/[^0-9]/g, '');
                      setProperty(p => ({ ...p, listing_price: raw ? parseInt(raw) : 0 }));
                    }}
                    className={`w-full pl-7 pr-3 py-2.5 border-2 rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary/50 font-medium ${errors.listing_price ? 'border-red-500' : 'border-primary/30'}`}
                    data-testid="listing-price-input"
                  />
                </div>
                {errors.listing_price && <p className="text-red-500 text-xs mt-1">{errors.listing_price}</p>}
              </div>
            )}
          </div>

          {/* FEATURE GAPS vs THE LOCAL COMPARABLES
              Worded as a difference from the neighbourhood, not as a missing feature. In
              a market where no house has a basement the user leaves this alone and the
              engine never deducts for something that is not the local norm. The amounts
              are internal (analysis_engine.FEATURE_GAP_ADJUSTMENTS) and deliberately not
              shown or sent — the user is declaring a fact about their block, not pricing
              it. Hidden in valuation mode for the same reason the purchase price is. */}
          {initialData.strategy !== 'valuation' && (
            <div className="mb-6 p-4 rounded-xl border border-border bg-muted/30"
                 data-testid="feature-gap-section">
              <h3 className="text-sm font-bold text-foreground mb-1">
                {lang === 'en'
                  ? 'Differences from the comparables nearby'
                  : 'Diferencias con los comparables de la zona'}
              </h3>
              <p className="text-xs text-muted-foreground mb-3">
                {lang === 'en'
                  ? 'Only tick these if homes selling around this one have the feature and this one does not. If none of them have it either, leave them unticked.'
                  : 'Marca solo si las casas que se venden alrededor tienen la característica y esta no. Si ninguna la tiene, déjalas sin marcar.'}
              </p>

              {/* BASEMENT */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={missingBasement !== null}
                  onChange={(e) => setMissingBasement(e.target.checked ? 'unfinished' : null)}
                  className="mt-0.5 w-4 h-4"
                  data-testid="missing-basement-checkbox"
                />
                <span className="text-sm text-foreground">
                  {lang === 'en'
                    ? 'The comparables nearby have a basement and this property does not'
                    : 'Los comparables de la zona tienen sótano y esta propiedad no'}
                </span>
              </label>
              {missingBasement !== null && (
                <div className="flex gap-2 mt-2 ml-6" data-testid="missing-basement-options">
                  {[{ v: 'unfinished', en: 'Unfinished', es: 'Sin terminar' },
                    { v: 'finished', en: 'Finished', es: 'Terminado' }].map(o => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setMissingBasement(o.v)}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${missingBasement === o.v ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                      data-testid={`missing-basement-${o.v}`}
                    >
                      {lang === 'en' ? o.en : o.es}
                    </button>
                  ))}
                </div>
              )}

              {/* GARAGE */}
              <label className="flex items-start gap-2 cursor-pointer mt-4">
                <input
                  type="checkbox"
                  checked={missingGarage !== null}
                  onChange={(e) => setMissingGarage(e.target.checked ? 1 : null)}
                  className="mt-0.5 w-4 h-4"
                  data-testid="missing-garage-checkbox"
                />
                <span className="text-sm text-foreground">
                  {lang === 'en'
                    ? 'The comparables nearby have a garage and this property does not'
                    : 'Los comparables de la zona tienen garaje y esta propiedad no'}
                </span>
              </label>
              {missingGarage !== null && (
                <div className="flex gap-2 mt-2 ml-6" data-testid="missing-garage-options">
                  {[{ v: 1, en: '1 space', es: '1 plaza' },
                    { v: 2, en: '2 spaces', es: '2 plazas' }].map(o => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setMissingGarage(o.v)}
                      className={`px-3 py-1.5 rounded-lg border text-sm transition-all ${missingGarage === o.v ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border text-muted-foreground hover:border-primary/50'}`}
                      data-testid={`missing-garage-${o.v}`}
                    >
                      {lang === 'en' ? o.en : o.es}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* MULTI-FAMILY INCOME — necessary data that drives a real ARV (multi types only) */}
          {isMultiFamilyType(property.property_type) && (
            <div className="mb-6 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50/40" data-testid="mf-income-inputs">
              <h3 className="text-sm font-bold text-emerald-800 mb-1 flex items-center gap-2">
                <DollarSign className="w-4 h-4" />
                {lang === 'en' ? 'Multi-Family Income (optional)' : 'Ingreso Multifamiliar (opcional)'}
              </h3>
              {/* WHAT THIS USED TO SAY, AND WHY IT WAS WRONG.
                  "Multi-family value is driven by income" / "(drives the ARV)" contradicted
                  the engine three lines below this file's own GRM comment: multifamily is
                  SALES-FIRST, and a blank GRM is the default validated against 12 real
                  appraisals across 5 states. The old copy described the OPTIONAL route as
                  if it were the normal one, and called leaving it blank a degraded result
                  ("you'll only see the conservative as-is value") when blank is in fact the
                  validated path. It pushed users toward typing a GRM, which is exactly the
                  input that hands 80% of the ARV weight to the income figure. */}
              <p className="text-xs text-slate-600 mb-3">
                {lang === 'en'
                  ? 'Multi-family value is calculated from comparable SALES by default. If you know the expected rent per unit after renovation and the area GRM, enter both and the ARV will be based on income instead. Leave them blank and the value comes from the sales comparables.'
                  : 'El valor multifamiliar se calcula por VENTAS comparables de forma predeterminada. Si conocés la renta esperada por unidad tras renovación y el GRM de la zona, ingresá ambos y el ARV pasará a basarse en el ingreso. Si los dejás en blanco, el valor sale de las ventas comparables.'}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {lang === 'en' ? 'Rent / Unit (after renovation)' : 'Renta / Unidad (tras renovación)'}
                  </label>
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">$</span>
                    <FormattedCurrencyInput
                      value={mfRentPerUnit}
                      onChange={(val) => setMfRentPerUnit(parseFormattedNumber(val) ?? '')}
                      prefix={null}
                      placeholder={lang === 'en' ? 'rent / unit / mo' : 'renta / unidad / mes'}
                      className="w-full px-3 py-2.5 border-2 border-emerald-300 rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-emerald-400 font-medium"
                      data-testid="mf-rent-per-unit-input"
                    />
                    <span className="text-xs text-slate-400 whitespace-nowrap">
                      × {unitsFromType(property.property_type)} {lang === 'en' ? 'units' : 'unidades'}
                    </span>
                  </div>
                  <RecommendedHint lang={lang}
                    en="Used only together with a GRM. On its own it does not change the ARV."
                    es="Se usa solo junto con un GRM. Por sí sola no cambia el ARV." />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    {lang === 'en' ? 'GRM (gross rent multiplier)' : 'GRM (multiplicador de renta bruta)'}
                  </label>
                  <input
                    type="number"
                    value={mfGrm}
                    onChange={(e) => setMfGrm(e.target.value)}
                    placeholder={lang === 'en' ? 'blank = value by sales' : 'vacío = valor por ventas'}
                    className="w-full px-3 py-2.5 border-2 border-emerald-300 rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-emerald-400 font-medium"
                    data-testid="mf-grm-input"
                  />
                  {String(mfGrm).trim() !== '' && (
                    <p className="mt-1 text-xs font-medium text-amber-700" data-testid="mf-grm-manual-label">
                      {lang === 'en'
                        ? 'Manual GRM — values by rents (after-repair), not sales comparables.'
                        : 'GRM manual — valora por rentas (tras reparación), no por comparables de venta.'}
                    </p>
                  )}
                  <RecommendedHint lang={lang}
                    en="Leave blank to value by sales comparables (default). Enter a GRM only to value by rents (after-repair)."
                    es="Déjalo en blanco para valorar por comparables de venta (predeterminado). Ingresa un GRM solo para valorar por rentas (tras reparación)." />
                </div>
              </div>
            </div>
          )}

          {/* Current Value / Asking Price - Only for valuation mode */}
          {initialData.strategy === 'valuation' && (
            <div className="pt-4 border-t border-border">
              <label className="block text-sm font-medium text-foreground mb-2">
                {lang === 'en' ? 'Current Value / Asking Price (optional)' : 'Valor Actual / Precio (opcional)'}
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                <input
                  type="text"
                  value={property.listing_price > 0 ? parseInt(property.listing_price).toLocaleString('en-US') : ''}
                  onChange={(e) => {
                    const value = e.target.value.replace(/,/g, '').replace(/[^0-9]/g, '');
                    setProperty(prev => ({ ...prev, listing_price: value ? parseInt(value) : 0 }));
                  }}
                  placeholder={lang === 'en' ? 'Optional' : 'Opcional'}
                  className="w-full pl-8 pr-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                  data-testid="valuation-current-value-input"
                />
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {lang === 'en' 
                  ? 'Used to calculate potential equity vs market value'
                  : 'Usado para calcular equidad potencial vs valor de mercado'}
              </p>
            </div>
          )}

          {/* Financing Mode Toggle - Hidden for valuation mode */}
          {initialData.strategy !== 'valuation' && (
            <div className="pt-4 border-t border-border">
              <label className="block text-sm font-medium text-foreground mb-2">
                {lang === 'en' ? 'Financing Mode' : 'Modo de Financiamiento'}
              </label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFinancingMode('cash')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                    financingMode === 'cash' 
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-semibold' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="confirm-financing-cash"
                >
                  <Banknote className="w-5 h-5" />
                  {lang === 'en' ? 'Cash' : 'Efectivo'}
                </button>
                <button
                  type="button"
                  onClick={() => setFinancingMode('financed')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                    financingMode === 'financed' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700 font-semibold' 
                      : 'border-border hover:border-primary/50'
                  }`}
                  data-testid="confirm-financing-financed"
                >
                  <CreditCard className="w-5 h-5" />
                  {lang === 'en' ? 'Financed' : 'Financiado'}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {lang === 'en' 
                  ? financingMode === 'cash' 
                    ? 'No loan costs — analysis shows pure cash returns'
                    : 'Includes loan costs — analysis reflects financing impact on profit'
                  : financingMode === 'cash'
                    ? 'Sin costos de préstamo — análisis muestra retornos en efectivo'
                    : 'Incluye costos de préstamo — análisis refleja impacto en ganancias'}
              </p>
            </div>
          )}
        </div>
        
        {/* ============================================== */}
        {/* SECONDARY SECTION: Strategy, Condition, Rehab */}
        {/* Collapsed by default - Advanced Analysis Settings */}
        {/* ============================================== */}
        <div className="bg-card rounded-xl border border-border mb-6">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full p-4 flex items-center justify-between text-left hover:bg-muted/30 transition-colors rounded-xl"
            data-testid="advanced-settings-toggle"
          >
            <div className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-muted-foreground" />
              <span className="font-medium text-foreground">
                {lang === 'en' ? 'Advanced Analysis Settings' : 'Configuración Avanzada de Análisis'}
              </span>
              <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded">
                {/* Say the BUDGET when there is one. With the presets gone this line read
                    "Auto Rehab" even while a typed budget was the number being used. */}
                {strategy === 'flip' ? 'Fix & Flip' : 'Rental'} • {
                  customRehabInputMode === 'total' && customRehabTotal
                    ? `$${customRehabTotal} ${lang === 'en' ? 'rehab' : 'remodelación'}`
                    : customRehabInputMode === 'persqft' && customRehabPerSqft
                    ? `$${customRehabPerSqft}/sqft ${lang === 'en' ? 'rehab' : 'remodelación'}`
                    : (lang === 'en' ? 'Auto Rehab' : 'Remodelación auto')
                }{strategy === 'flip' ? ` • ${targetRule}% Rule` : ''}
              </span>
            </div>
            <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
          </button>
          
          {/* Collapsible content */}
          {showAdvanced && (
            <div className="p-6 pt-0 border-t border-border">
              {/* Investment Strategy - Hidden for valuation mode */}
              {initialData.strategy !== 'valuation' && (
                <>
                  <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2 mt-4">
                    <TrendingUp className="w-4 h-4" />
                    {lang === 'en' ? 'Investment Strategy' : 'Estrategia de Inversión'}
                  </h3>
          
          <div className="grid grid-cols-2 gap-4 mb-6">
            <button
              onClick={() => setStrategy('flip')}
              className={`p-4 rounded-lg border-2 transition-all ${strategy === 'flip' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
              data-testid="strategy-flip-btn"
            >
              <Wrench className={`w-6 h-6 mb-2 ${strategy === 'flip' ? 'text-primary' : 'text-muted-foreground'}`} />
              <div className={`font-medium ${strategy === 'flip' ? 'text-primary' : 'text-foreground'}`}>
                Fix & Flip
              </div>
              <div className="text-xs text-muted-foreground">
                {lang === 'en' ? 'Buy, renovate, sell' : 'Comprar, renovar, vender'}
              </div>
            </button>
            
            <button
              onClick={() => setStrategy('rental')}
              className={`p-4 rounded-lg border-2 transition-all ${strategy === 'rental' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
              data-testid="strategy-rental-btn"
            >
              <Building className={`w-6 h-6 mb-2 ${strategy === 'rental' ? 'text-primary' : 'text-muted-foreground'}`} />
              <div className={`font-medium ${strategy === 'rental' ? 'text-primary' : 'text-foreground'}`}>
                Rental
              </div>
              <div className="text-xs text-muted-foreground">
                {lang === 'en' ? 'Buy & hold' : 'Comprar y alquilar'}
              </div>
            </button>
          </div>
                </>
              )}
          
          {/* CONDITION — rentals only, and the only condition control left.
              It drives dscr_eligible, the forced-cash fallback and the per-mode rehab
              figure. Same control the Analyzer shows (RENTAL_CONDITIONS), repeated here
              where the decision is confirmed, so the vocabulary does not change.

              The generic scale (distressed/average/updated/fully_renovated) that used to
              occupy the other half of this branch is gone from the product. It changed no
              number in flip — the engine forces 'average' there, and all five options
              returned the identical ARV on every property measured — and in rentals it let
              the same house be declared turnkey for the loan and distressed for the value
              at the same time. The two were never mergeable: mapping between them meant
              inventing an equivalence nobody defined. Removing one settled it. */}
          {strategy === 'rental' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                {lang === 'en' ? 'Condition for Financing' : 'Condición para el financiamiento'}
              </label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {RENTAL_CONDITIONS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setRentalPropertyCondition(opt.value)}
                    className={`p-3 rounded-lg border text-left transition-all ${rentalPropertyCondition === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`}
                    data-testid={`rental-condition-${opt.value}`}
                  >
                    <div className={`font-medium text-sm ${rentalPropertyCondition === opt.value ? 'text-primary' : 'text-foreground'}`}>
                      {lang === 'en' ? opt.en : opt.es}
                    </div>
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {lang === 'en'
                  ? 'Heavy or Distressed is not eligible for DSCR financing — the analysis switches to cash.'
                  : 'Pesado o En mal estado no califica para financiamiento DSCR — el análisis pasa a efectivo.'}
              </p>
            </div>
          )}
          
          {/* Rehab Estimate (for Flip only) */}
          {strategy === 'flip' && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-foreground mb-2">
                {lang === 'en' ? 'Rehab Estimate' : 'Estimación de Remodelación'}
              </label>
              <RecommendedHint lang={lang}
                en="Renovation cost directly affects your profit — set it to your real budget."
                es="El costo de renovación afecta directamente tu ganancia — ajústalo a tu presupuesto real." />
              {/* REHAB BUDGET — the Auto/Light/Medium/Heavy presets were removed on a
                  lender's recommendation. UI only: rehabCondition stays 'auto' and is still
                  sent, so a blank budget yields the auto-detected estimate, not $0. The
                  amount already outranked the label (handleRunAnalysis below sends it
                  whatever the condition says), so a typed budget gives the same number. */}
              <div className="bg-background border border-border rounded-lg p-4 animate-fade-in mt-2">
                  <div className="flex items-center gap-4 mb-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="rehab-mode"
                        checked={customRehabInputMode === 'total'}
                        onChange={() => setCustomRehabInputMode('total')}
                        className="w-4 h-4 text-primary"
                        data-testid="confirm-rehab-mode-total"
                      />
                      <span className={customRehabInputMode === 'total' ? 'font-medium' : 'text-muted-foreground'}>
                        {lang === 'en' ? 'Total Budget' : 'Presupuesto Total'}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="rehab-mode"
                        checked={customRehabInputMode === 'persqft'}
                        onChange={() => setCustomRehabInputMode('persqft')}
                        className="w-4 h-4 text-primary"
                        data-testid="confirm-rehab-mode-persqft"
                      />
                      <span className={customRehabInputMode === 'persqft' ? 'font-medium' : 'text-muted-foreground'}>
                        $/sqft
                      </span>
                    </label>
                  </div>
                  
                  {customRehabInputMode === 'total' ? (
                    <div className="max-w-xs">
                      {/* BUG 1: $0,000,000 formatting on the rehab budget. Stored as a
                          formatted string; handleRunAnalysis strips $/commas before calc. */}
                      <FormattedCurrencyInput
                        value={customRehabTotal}
                        onChange={(val) => setCustomRehabTotal(val)}
                        prefix="$"
                        placeholder="e.g., 50,000"
                        className="w-full pr-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                        data-testid="confirm-custom-rehab-total"
                      />
                    </div>
                  ) : (
                    <div className="relative max-w-xs">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <input
                        type="number"
                        value={customRehabPerSqft}
                        onChange={(e) => setCustomRehabPerSqft(e.target.value)}
                        placeholder="e.g., 35"
                        className="w-full pl-8 pr-16 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
                        data-testid="confirm-custom-rehab-persqft"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">/sqft</span>
                    </div>
                  )}
                </div>
            </div>
          )}
          
          {/* Target Rule (for Flip) */}
          {strategy === 'flip' && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {lang === 'en' ? 'Target Purchase Rule' : 'Regla de Compra Objetivo'}
              </label>
              <div className="flex items-center gap-3">
                {[70, 75, 80].map(rule => (
                  <button
                    key={rule}
                    onClick={() => setTargetRule(rule)}
                    className={`px-4 py-2 rounded-lg border transition-all ${targetRule === rule ? 'border-primary bg-primary/5 text-primary font-medium' : 'border-border hover:border-primary/50'}`}
                    data-testid={`target-rule-${rule}-btn`}
                  >
                    {rule}% Rule
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {lang === 'en' 
                  ? `Target = ${targetRule}% of ARV - Rehab Costs`
                  : `Objetivo = ${targetRule}% del ARV - Costos de Remodelación`}
              </p>
            </div>
          )}
          </div>
          )}
        </div>
        
        {/* Run Analysis Button */}
        <div className="flex justify-end gap-4">
          <button
            onClick={handleBack}
            className="px-6 py-3 border border-border rounded-lg hover:bg-muted transition-colors"
            disabled={analyzing}
          >
            {lang === 'en' ? 'Back' : 'Atrás'}
          </button>
          
          <button
            onClick={handleRunAnalysis}
            disabled={analyzing}
            className="px-8 py-3 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="run-analysis-btn"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {lang === 'en' ? 'Analyzing...' : 'Analizando...'}
              </>
            ) : (
              <>
                {lang === 'en' ? 'Run Analysis' : 'Ejecutar Análisis'}
                <ChevronRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyConfirmation;
